import { useMemo } from 'react';
import { auth } from 'services/aws';

import userRole from 'config/userRole';
import Store from 'helpers/store';
import useStore from 'hooks/useStore';
import ReactGA from 'react-ga4';

const createAuthActions = (store) => {
  const mapEmailToUserId = (email) => {
    const [username] = email.split('@');
    return username || 'Not Found';
  };

  const mapUserToStore = (user) => {
    const userInfo = {
      username: user.username,
      cognitoUser: user,
    };
    
    if (user.signInUserSession) {
      const idToken = user.signInUserSession.getIdToken();      
      if (idToken && idToken.payload) {
        const userGroups = idToken.payload['cognito:groups'] || [];
        userInfo.role = userGroups[0] || userRole.USER;
        userInfo.displayName = idToken.payload['custom:display_name'] || user.username;
        userInfo.userId = mapEmailToUserId(user.username);
      }
    }
    return userInfo;
  };

  return {
    async login({ username, password }) {
      return auth.signIn(username, password).then(user => {
        const userInfo = mapUserToStore(user);

        window.gtag('set', {
          'user_id': userInfo.userId,
        });

        ReactGA.set({ 'user_id': userInfo.userId });

        store.set(() => ({
          currentUser: userInfo,
          isAuthenticated: true,
          requiresPassReset: user.challengeName === 'NEW_PASSWORD_REQUIRED',
        }));
      });
    },

    async logout() {
      return auth.signOut().then(() => {
        window.gtag('set', {
          'user_id': null,
        });

        ReactGA.set({ 'user_id': undefined });

        store.set(() => ({
          currentUser: null,
          isAuthenticated: false,
          requiresPassReset: false,
        }));
      });
    },

    async loggedInUser() {
      const user = await auth.currentAuthenticatedUser();
      if (user) {
        const userInfo = mapUserToStore(user);

        window.gtag('set', {
          'user_id': userInfo.userId,
        });

        ReactGA.set({ 'user_id': userInfo.userId });

        store.set({
          currentUser: userInfo,
          isAuthenticated: true,
        });
        return true;
      } else {
        store.reset();

        window.gtag('set', {
          'user_id': null,
        });

        ReactGA.set({ user_id: undefined });
        
        return false;
      }
    },

    async completePasswordResetChallenge({ password }) {
      const { currentUser } = store.get();
      if (!currentUser || !currentUser.cognitoUser) {
        const err = new Error('No cognito user');
        err.code = 'INVALID_RESET_FLOW';
        throw err;
      }

      return auth.completeNewPassword(currentUser.cognitoUser, password).then(() => {
        store.set(() => ({
          requiresPassReset: false,
        }));
      });
    },

    async requestPasswordReset({ username }) {
      return auth.forgotPassword(username).then(() => {
        store.set(() => ({
          currentUser: { username }
        }));
      })
    },

    async completePasswordReset({ resetCode, password }) {
      const { currentUser } = store.get();
      if (!currentUser || !currentUser.username) {
        const err = new Error('No cognito user');
        err.code = 'INVALID_RESET_FLOW';
        throw err;
      }
      return auth.forgotPasswordSubmit(currentUser.username, resetCode, password);
    },

    async changePassword({ currentPassword, password }) {
      const { currentUser } = store.get();
      if (!currentUser || !currentUser.cognitoUser) {
        const err = new Error('No cognito user');
        err.code = 'INVALID_RESET_FLOW';
        throw err;
      }

      return auth.changePassword(currentUser.cognitoUser, currentPassword, password);
    }
  }
};

const authStore = new Store({
  currentUser: {
    displayName: 'guest',
    role: userRole.GUEST,
  },
  isAuthenticated: false,
  requiresPassReset: false,
}, 'auth');

const useAuth = () => {
  const actions = useMemo(() => {
    return createAuthActions(authStore);
  }, []);

  useStore(authStore);

  return {
    state: authStore.get(),
    store: authStore,
    actions,
  };
};

export default useAuth;